import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettingsViewModel } from '../viewmodels/SettingsViewModel';
import { BigButton, FIELD, FieldLabel, ICONS, LineIcon, Modal, SectionLabel, StatusPill } from '../components/ui/dg';

/**
 * Paramètres du back-office. N'y figure que ce qui agit vraiment : l'heure
 * affichée, le mot de passe (fenêtre dédiée) et la déconnexion. Les anciens
 * réglages (langue, thème, devise, notifications) n'étaient gardés que dans
 * le navigateur et ne changeaient rien : ils sont retirés.
 */

const ROLE_LABEL: Record<string, string> = { admin: 'Administrateur', moderator: 'Modérateur', user: 'Utilisateur' };

const Row: React.FC<{ icon: string; tone?: 'indigo' | 'grey' | 'red'; title: string; sub?: string; first?: boolean; right?: React.ReactNode; onClick?: () => void }> = ({
  icon, tone = 'indigo', title, sub, first, right, onClick,
}) => {
  const [bg, fg] = { indigo: ['#EEEDFD', '#4F46E5'], grey: ['#F1F2F8', '#475569'], red: ['#FDECEF', '#B91C1C'] }[tone];
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      className={`flex w-full items-center gap-3.5 py-[13px] text-left ${first ? '' : 'border-t border-[#EEF0F4]'}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: bg }}>
        <LineIcon d={(ICONS as any)[icon] || icon} size={18} color={fg} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-[14px] font-bold ${tone === 'red' ? 'text-[#B91C1C]' : 'text-[#1E293B]'}`}>{title}</span>
        {sub && <span className="mt-0.5 block text-[12.5px] text-[#6B7280]">{sub}</span>}
      </span>
      {right ?? (onClick ? <LineIcon d={ICONS.chevron} size={16} color="#9CA3AF" width={2.2} /> : null)}
    </Tag>
  );
};

/** Solidité : longueur, chiffres, majuscules et symboles. */
const strength = (value: string): { score: number; label: string; color: string } => {
  if (value.length < 6) return { score: value ? 1 : 0, label: 'Trop court, 6 caractères au moins.', color: '#DC2626' };
  let score = 1;
  if (value.length >= 10) score += 1;
  if (/\d/.test(value) && /[A-Za-z]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value) || /[A-Z]/.test(value)) score += 1;
  return score >= 4 ? { score, label: 'Solide.', color: '#10B981' }
    : score === 3 ? { score, label: 'Correct. Ajoutez un symbole ou une majuscule.', color: '#10B981' }
    : { score, label: 'Faible. Allongez-le et mélangez lettres et chiffres.', color: '#F59E0B' };
};

const PasswordModal = ({ onClose }: { onClose: () => void }) => {
  const { user } = useAuth();
  const { changePassword, validatePasswordForm } = useSettingsViewModel();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const s = strength(next);

  const submit = async () => {
    const check = validatePasswordForm({ currentPassword: current, newPassword: next, confirmPassword: confirm });
    if (!check.isValid) { setMessage({ ok: false, text: check.errors[0] }); return; }
    setBusy(true);
    setMessage(null);
    const result = await changePassword(String((user as any)?.id_user || ''), current, next);
    setBusy(false);
    if (result.success) { setMessage({ ok: true, text: 'Mot de passe modifié.' }); setTimeout(onClose, 900); }
    else setMessage({ ok: false, text: result.message });
  };

  return (
    <Modal title="Changer le mot de passe" subtitle="Celui que vous utilisez pour ouvrir le back-office." onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <FieldLabel htmlFor="pw-current" className="mt-6">Mot de passe actuel</FieldLabel>
        <input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={FIELD} />
        <FieldLabel htmlFor="pw-next">Nouveau mot de passe</FieldLabel>
        <input id="pw-next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={FIELD} />
        {next && (
          <>
            <div className="mt-2.5 flex gap-1.5" aria-hidden="true">
              {[1, 2, 3, 4].map((i) => <span key={i} className="h-[5px] flex-1 rounded-full" style={{ background: i <= s.score ? s.color : '#E5E7EB' }} />)}
            </div>
            <p className="mt-1.5 text-[12.5px] font-semibold" style={{ color: s.color === '#10B981' ? '#047857' : s.color === '#F59E0B' ? '#B45309' : '#B91C1C' }}>{s.label}</p>
          </>
        )}
        <FieldLabel htmlFor="pw-confirm">Confirmer</FieldLabel>
        <input id="pw-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={FIELD} />
        {confirm && confirm !== next && <p className="mt-1.5 text-[12.5px] font-semibold text-[#B91C1C]">Les deux mots de passe ne sont pas identiques.</p>}
        {message && <p className={`mt-4 rounded-xl px-3 py-2 text-[13px] ${message.ok ? 'bg-[#ECFDF5] text-[#047857]' : 'bg-[#FDECEF] text-[#B91C1C]'}`}>{message.text}</p>}
        <div className="mt-6 flex gap-3">
          <BigButton kind="secondary" onClick={onClose} className="flex-1">Annuler</BigButton>
          <BigButton type="submit" disabled={busy || !current || !next || next !== confirm} className="flex-1">{busy ? 'Enregistrement…' : 'Enregistrer'}</BigButton>
        </div>
      </form>
    </Modal>
  );
};

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const u = user as any;

  return (
    <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        <section className="rounded-[20px] border border-[#EEF0F4] bg-white px-6 pb-2.5 pt-[22px] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <SectionLabel>Préférences</SectionLabel>
          <div className="mt-1.5">
            <Row first icon={ICONS.flag} tone="grey" title="Heures affichées" sub="Heure de Tunis, quel que soit le pays" right={<StatusPill tone="grey">UTC+1</StatusPill>} />
            <Row icon={ICONS.user} tone="grey" title="Rôle" sub="Ce que vous pouvez voir et faire dans le back-office" right={<StatusPill tone="indigo">{ROLE_LABEL[u?.role] || u?.role || '—'}</StatusPill>} />
          </div>
        </section>

        <section className="rounded-[20px] border border-[#EEF0F4] bg-white px-6 pb-2.5 pt-[22px] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <SectionLabel>Sécurité</SectionLabel>
          <div className="mt-1.5">
            <Row first icon={ICONS.lock} title="Mot de passe" sub="Celui de votre compte Dingo" onClick={() => setPasswordOpen(true)} />
            <Row icon="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10" tone="red" title="Se déconnecter" sub={u?.email || undefined} onClick={() => logout()} />
          </div>
        </section>
      </div>
      {passwordOpen && <PasswordModal onClose={() => setPasswordOpen(false)} />}
    </main>
  );
};

export default Settings;
